import type { CSSProperties, Dispatch, SetStateAction } from 'react'
import { Form, IteratorField, RangeField, SortField } from '@epages/react-components'
import { createElement, useEffect, useRef, useState } from 'react'
import { kebabCase } from 'lodash-es'
import Immutable from 'immutable'
import cc from 'classcat'

import { formatHref } from '../../utils/link-sanitizer'
import { getTextPersistanceInfo, normalizeButtonState } from '../image/ImageSettings'
import { getTitle } from '../../utils/get-plugin-title'
import { imageColorTextSettingsDefaults } from '../../../../../utils/hooks/colorSettings'
import { trimData } from '../../utils/trim-data'
import { useTranslate } from '../../../../../utils/translate'
import { withImageData, withMergedImageData } from '../image/ImageField'
import Button from '../../../../formComponents/Button'
import ButtonRow from '../../../../formComponents/ButtonRow'
import FormValue from '../../../../formComponents/FormValue'
import ImageSettings from '../components/ImageSettings'
import ImageTextColorSettings from '../components/ImageTextColorSettings'
import ImageTextSettings from '../components/ImageTextSettings'
import useHelpCenterLink from '../../../../../utils/hooks/useHelpCenterLink'

const emptyImage = Immutable.fromJS({
  src: '',
  width: null,
  height: null,
  alt: '',
  opentab: false,
  alignment: 'center',
})

const imageTextSettingsDefaults = Immutable.Map({
  link: '',
  text: '',
  buttontext: '',
  headline: '',
  buttonenabled: false,
})

function removeEmptyImages(images) {
  return images.filter((image) => image.getIn(['imageData', 'src']))
}

function padWithEmptyImages(images) {
  return images.concat(Immutable.Repeat(emptyImage, 5 - images.count()))
}

function prepareImagesTextSettings(images) {
  return images.map((image) => image.mergeDeep(imageTextSettingsDefaults, imageColorTextSettingsDefaults, image))
}

function normalize(value) {
  return value.update('images', (images) =>
    removeEmptyImages(images.map(withMergedImageData)).map((image) =>
      trimData(image).update('link', formatHref).remove('imageData'),
    ),
  )
}

const tabs = {
  imageSettings: ImageSettings,
  imageTextSettings: ImageTextSettings,
  imageTextColorSettings: ImageTextColorSettings,
}

type Props = {
  selectedIndex?: number
  onSelectionChange: Dispatch<SetStateAction<number>>
} & WorkspacePluginSettingsProps

export default function ContentSliderSettings({
  selectedIndex,
  config,
  data,
  onSelectionChange,
  onDataChange,
  onSave,
  onCancel,
  renderLayout,
}: Readonly<Props>) {
  const [error, setError] = useState<(Error & { status: number }) | null>(null)
  const [activeTab, setActiveTab] = useState('imageSettings')
  const selectImageRef = useRef<HTMLDivElement>(null)
  const helpCenterUrl = useHelpCenterLink('IMAGE_FORMATS')

  const isUnsupportedMediaType = error?.code === 415
  const previewUrl = config?.previewUrl || ((url) => url)
  const nonEmptyPredicate = (i) => i.getIn(['imageData', 'src'])
  const emptyPredicate = (i) => !nonEmptyPredicate(i)

  const slideIntervalMin = 1000
  const slideIntervalMax = 10000

  const t = useTranslate('interface', 'components.contentSliderComponent.settingsModal')

  useEffect(() => {
    const setSortFieldSelectedItemClass = () => {
      // Toggles "selected-item" CSS class on `SortField` items.
      // This is needed to work around "zoom" behavior in CSS.
      // react-components `SortField` uses CSS inline styles on each item, and it
      // is not possible to access its node in JSX directly.

      selectImageRef.current?.querySelectorAll('.sort-field > div').forEach((node, index) => {
        node.classList.toggle('selected-item', selectedIndex === index)
      })
    }

    setSortFieldSelectedItemClass()
  }, [selectedIndex])

  const renderTabs = () => (
    <div className="dali-settingslayer-tabs">
      {Object.keys(tabs).map((tab) => (
        <button
          key={tab}
          type="button"
          className={cc([`dali-settingslayer-tabs-${kebabCase(tab)}`, { active: activeTab === tab }])}
          title={getTitle({
            tab,
            tScoped: t,
            isEditActive: data.get('images').size > 0,
          })}
          onClick={() => setActiveTab(tab)}
        />
      ))}
    </div>
  )

  const renderTabBody = (image) => {
    const props = {
      config,
      handleError: (error) => {
        // handleError passes a plain object with an `error` key that contains the error
        setError(error.error)
      },
      imageInfo: {
        isSlider: true,
        src: image.getIn(['imageData', 'src']),
      },
      textPersistanceInfo: getTextPersistanceInfo(image),
    }

    return createElement(tabs[activeTab], props)
  }

  const body = (value) => (
    <>
      {error && (
        <div className="dali-settingslayer-element">
          <div className="dali-notification-danger">
            {error.message}{' '}
            {isUnsupportedMediaType && (
              <a
                href={helpCenterUrl}
                target="_blank"
                className="ep-form-row-text-external-link"
                rel="noreferrer noopener"
              >
                {t(':components.imageUploadComponent.imageField.errorMessages.helpCenterLink')}
              </a>
            )}
          </div>
        </div>
      )}
      <div ref={selectImageRef} className="dali-settingslayer-element dali-settingslayer-select-image">
        <SortField
          className="sort-field"
          name="images"
          onReorder={(_oldIndex, newIndex) => onSelectionChange(newIndex)}
          orientation="horizontal"
          itemSize={60}
          crossAxisItemSize={33}
          itemSpacing={13}
          itemCount={5}
          validate={(images) => {
            // ensure that there are no holes in the array (i.e. no empty images between non-empty images)
            return images.takeWhile(nonEmptyPredicate).count() === images.filter(nonEmptyPredicate).count()
          }}
          disabled={(image, index, images) => {
            // disable when current and previous image is empty
            return emptyPredicate(image) && index > 0 && emptyPredicate(images.get(index - 1))
          }}
        >
          {(image, index) => {
            const src = image.getIn(['imageData', 'src'])
            const empty = !src?.trim()?.length
            const disabled = image.get('__isDisabled')
            const selected = selectedIndex === index
            const draggedOver = image.get('__isTarget')

            return (
              <div
                onClick={() => !disabled && onSelectionChange(index)}
                style={{ pointerEvents: image.get('__isDragging') && !image.get('__isSource') ? 'none' : 'auto' }}
                className={cc([
                  'dali-settingslayer-select-image-item',
                  {
                    empty,
                    disabled,
                    selected,
                    'dragged-over': draggedOver,
                  },
                ])}
              >
                <div
                  style={
                    {
                      backgroundImage: config?.imageUrl && !empty ? `url("${previewUrl(config.imageUrl(src))}")` : null,
                    } as CSSProperties
                  }
                />
              </div>
            )
          }}
        </SortField>
      </div>
      <div className="dali-settingslayer-contentslider-slide-interval">
        <label className="dali-settingslayer-label">
          {t('slideDurationRangeInput.label', { count: value.get('slideInterval') / 1000 })}
          <FormValue name="slideInterval">
            {(value) => (
              <RangeField
                className="dali-range-input"
                name="slideInterval"
                min={slideIntervalMin}
                max={slideIntervalMax}
                step={1000}
                style={{
                  '--min': slideIntervalMin,
                  '--max': slideIntervalMax,
                  '--val': value,
                }}
              />
            )}
          </FormValue>
        </label>
      </div>
      {renderTabs()}
      <scroll-shadow>
        <div className="dali-settingslayer-main-content">
          <IteratorField name="images" skip={selectedIndex} take={1}>
            {(image) => renderTabBody(image)}
          </IteratorField>
        </div>
      </scroll-shadow>
    </>
  )

  const footer = (
    <ButtonRow>
      <Button type="button" variant="cancel" onClick={onCancel}>
        {t('cancelButton.label')}
      </Button>
      <Button>{t('saveButton.label')}</Button>
    </ButtonRow>
  )

  const formData = data.update('images', (images) =>
    images.map((image) => withImageData(normalizeButtonState(emptyImage.merge(image)))),
  )

  return (
    <Form
      value={formData}
      name="pluginContentSliderSettings"
      onSubmit={onSave}
      onChange={(data) =>
        onDataChange(
          data.update('images', (images) =>
            removeEmptyImages(images).map((image) => withMergedImageData(normalizeButtonState(image))),
          ),
        )
      }
      prepare={(data) =>
        data.update('images', (images) =>
          prepareImagesTextSettings(padWithEmptyImages(removeEmptyImages(images))).map(withImageData),
        )
      }
      normalize={normalize}
    >
      {({ value }) =>
        renderLayout({
          title: getTitle({
            tab: activeTab,
            tScoped: t,
            isEditActive: data.get('images').size > 0,
          }),
          explanation: activeTab === 'imageSettings' ? t('explanation') : undefined,
          body: body(value),
          footer,
        })
      }
    </Form>
  )
}
